
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Button } from "@/components/ui/button";
import { Plus, RefreshCw } from "lucide-react";
import { useState, useEffect } from "react";
import { QuotationForm } from "@/components/QuotationForm";
import { QuotationTable } from "@/components/QuotationTable";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase, trackEvent, RealtimePayload } from "@/integrations/supabase/client";

export function QuotationsPage() {
  const [showQuotationForm, setShowQuotationForm] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();
  const [channelName] = useState(`quotation-page-${Math.random().toString(36).substring(2, 10)}`);

  // Set up global real-time listener for quotations
  useEffect(() => {
    console.log(`QuotationsPage: Setting up channel ${channelName}`);
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "quotations",
        },
        async (payload: RealtimePayload) => {
          console.log("QuotationsPage detected change:", payload.eventType);
          
          // Safely get the event ID
          const eventId = payload.new?.id || payload.old?.id;
          if (!eventId) {
            console.log('QuotationsPage: No event ID found, skipping');
            return;
          }
          
          const isProcessed = trackEvent(`${payload.eventType}-quotation`, eventId);
          if (isProcessed) {
            console.log(`QuotationsPage: Event ${payload.eventType}-${eventId} already processed, skipping`);
            return;
          }
          
          // Get current user id safely
          const { data } = await supabase.auth.getUser();
          const currentUserId = data.user?.id;
          
          // Safely extract change user id
          const changeUserId = payload.new?.created_by || payload.old?.created_by;
          
          // Show toast notification based on event type if not triggered by current user
          if (currentUserId !== changeUserId) {
            if (payload.eventType === 'INSERT') {
              toast.info("Novo orçamento adicionado");
            } else if (payload.eventType === 'UPDATE') {
              toast.info("Um orçamento foi atualizado");
            } else if (payload.eventType === 'DELETE') {
              toast.info("Um orçamento foi removido");
            }
          }
          
          // Invalidate queries
          queryClient.invalidateQueries({ queryKey: ["quotations"] });
        }
      )
      .subscribe((status) => {
        console.log('QuotationsPage real-time subscription status:', status);
      });

    return () => {
      console.log(`QuotationsPage: Cleaning up channel ${channelName}`);
      supabase.removeChannel(channel);
    };
  }, [queryClient, channelName]);

  const handleEdit = (quotation: any) => {
    setEditingQuotation(quotation);
    setShowQuotationForm(true);
  };

  const handleCloseForm = () => {
    setShowQuotationForm(false);
    setEditingQuotation(null);
  };

  // Function to manually refresh all quotation data
  const refreshQuotations = async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ["quotations"] });
      toast.success("Dados atualizados com sucesso");
    } catch (error) {
      console.error("Erro ao atualizar orçamentos:", error);
      toast.error("Erro ao atualizar dados");
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="flex h-screen w-full">
      <DashboardSidebar />
      <div className="flex-1 flex flex-col">
        <header className="flex justify-between items-center px-6 py-4 bg-[#1A1F2C] border-b border-gray-700">
          <h2 className="text-2xl font-bold font-['Russo_One']">Orçamentos</h2>
          <div className="flex gap-2">
            {isRefreshing ? (
              <Button 
                className="bg-gray-600 text-white cursor-not-allowed"
                disabled
              >
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Atualizando...
              </Button>
            ) : (
              <Button 
                onClick={refreshQuotations}
                className="bg-[#0073B7] text-white hover:bg-[#004080]"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Atualizar
              </Button>
            )}
            <Button 
              onClick={() => setShowQuotationForm(true)}
              className="bg-white text-[#1A1F2C] hover:bg-gray-100"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Orçamento
            </Button>
          </div>
        </header>

        <main className="flex-1 bg-[#1A1F2C]">
          <QuotationTable onEdit={handleEdit} />
        </main>
      </div>

      {showQuotationForm && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50">
          <div className="fixed inset-4 md:inset-x-[15%] lg:inset-x-[20%] md:inset-y-[10%] bg-card rounded-lg shadow-lg overflow-hidden">
            <div className="h-full flex flex-col">
              <div className="flex justify-between items-center p-6 border-b">
                <h2 className="text-2xl font-bold font-['Russo_One']">
                  {editingQuotation ? "Editar" : "Novo"} Orçamento
                </h2>
                <button
                  onClick={handleCloseForm}
                  className="text-muted-foreground hover:text-foreground"
                >
                  ✕
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <QuotationForm
                  onClose={handleCloseForm}
                  initialData={editingQuotation}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
